import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button, Modal, message } from "antd"
import { BarChartOutlined, PlusOutlined } from "@ant-design/icons"
import { useSelector } from "react-redux"
import { jwtDecode } from "jwt-decode"
import { translations } from "../../utils/Translation/staticTranslation"
import { getUserCookies } from "../../utils/Cookies"
import {
  canDeleteInModule,
  isPlansPricingReadOnly,
} from "../../utils/modulePermissionSidebar"
import PlansPricingTable from "../../components/PlansPricing/PlansPricingTable"
import { PlansApi } from "../../Apis/plansApi"
import {
  mapAdminPlanToTableRow,
  parseAdminPlansListMeta,
  parseAdminPlansListResponse,
} from "../../utils/plansPricingNormalize"
import {
  getPlansApiError,
  getPlansApiSuccessMessage,
} from "../../utils/plansPricingPayload"
import { getGridPageSize } from "../../utils/plansPricingGrid"
import "../../components/PlansPricing/PlansPricing.css"

const LIST_PAGE_SIZE = 10

function getInitialViewportWidth() {
  if (typeof window === "undefined") return 1200
  return window.innerWidth
}

const PlansPricing = () => {
  const navigate = useNavigate()
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.plansPricingPage

  const user = useSelector((state) => state.user.user)
  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const isReadOnly = isPlansPricingReadOnly(user, decoded)
  const canDelete =
    !isReadOnly && canDeleteInModule(user, "plans-pricing", decoded)

  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [typeFilter, setTypeFilter] = useState("all")
  const [categoryFilter, setCategoryFilter] = useState("all")
  const [viewMode, setViewMode] = useState("list")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(LIST_PAGE_SIZE)
  const [total, setTotal] = useState(0)
  const [viewportWidth, setViewportWidth] = useState(getInitialViewportWidth)

  const gridPageSize = useMemo(
    () => getGridPageSize(viewportWidth),
    [viewportWidth]
  )
  const effectivePageSize = viewMode === "grid" ? gridPageSize : pageSize

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const fetchPlans = useCallback(async () => {
    setLoading(true)
    try {
      const response = await PlansApi.getPlansList({
        search,
        status: statusFilter,
        type: typeFilter,
        planCategory: categoryFilter,
        page,
        pageSize: effectivePageSize,
      })

      if (response?.status === 200) {
        const raw = parseAdminPlansListResponse(response.data)
        const meta = parseAdminPlansListMeta(response.data)
        setPlans(raw.map((plan) => mapAdminPlanToTableRow(plan, lang)))
        setTotal(meta.count)
      } else {
        setPlans([])
        setTotal(0)
      }
    } catch (error) {
      console.error("Error fetching plans:", error)
      setPlans([])
      setTotal(0)
      message.error(
        t?.error_fetching_plans || "Error fetching plans. Please try again."
      )
    } finally {
      setLoading(false)
    }
  }, [
    search,
    statusFilter,
    typeFilter,
    categoryFilter,
    page,
    effectivePageSize,
    lang,
    t?.error_fetching_plans,
  ])

  useEffect(() => {
    fetchPlans()
  }, [fetchPlans])

  useEffect(() => {
    if (viewMode === "grid") {
      setPage(1)
    }
  }, [gridPageSize, viewMode])

  const handleSearchChange = (value) => {
    setSearch(value)
    setPage(1)
  }

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value)
    setPage(1)
  }

  const handleTypeFilterChange = (value) => {
    setTypeFilter(value)
    setPage(1)
  }

  const handleCategoryFilterChange = (value) => {
    setCategoryFilter(value)
    setPage(1)
  }

  const handleViewModeChange = (mode) => {
    setViewMode(mode)
    if (mode === "list") {
      setPageSize(LIST_PAGE_SIZE)
    }
    setPage(1)
  }

  const handlePaginationChange = (nextPage, nextPageSize) => {
    setPage(nextPage)
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize)
      setPage(1)
    }
  }

  const notifyReadOnly = () => {
    message.info(
      t?.read_only_action_blocked ||
        "View only — you cannot perform this action."
    )
  }

  const notifyDeleteBlocked = () => {
    if (isReadOnly) {
      notifyReadOnly()
      return
    }
    message.info(
      translations[lang]?.common_error_msg?.delete_not_allowed ||
        "Your role does not allow deleting in this section."
    )
  }

  const handleCreatePlan = () => {
    if (isReadOnly) {
      notifyReadOnly()
      return
    }
    navigate("/plans-pricing/create")
  }

  const handleEdit = (record) => {
    if (isReadOnly) {
      notifyReadOnly()
      return
    }
    if (!record?.id) return
    navigate(`/plans-pricing/edit/${record.id}`)
  }

  const handleDuplicate = async (record) => {
    if (isReadOnly) {
      notifyReadOnly()
      return
    }
    if (!record?.id) return

    setActionLoading(true)
    try {
      const response = await PlansApi.duplicatePlan(record.id, {
        duplicate: true,
      })
      if (response?.status === 200) {
        message.success(
          getPlansApiSuccessMessage(
            response.data,
            lang,
            "Plan duplicated successfully.",
            "تم نسخ الباقة بنجاح."
          )
        )
        fetchPlans()
      }
    } catch (error) {
      console.error("Error duplicating plan:", error)
      message.error(
        getPlansApiError(error, lang) ||
          t?.error_duplicating_plan ||
          "Error duplicating plan."
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleDisable = async (record) => {
    if (isReadOnly) {
      notifyReadOnly()
      return
    }
    if (!record?.id || record.status !== "Active") return

    setActionLoading(true)
    try {
      const response = await PlansApi.patchPlan(record.id, {
        status: "Inactive",
        active_status: false,
      })
      if (response?.status === 200) {
        message.success(
          getPlansApiSuccessMessage(
            response.data,
            lang,
            "Plan disabled successfully.",
            "تم تعطيل الباقة بنجاح."
          )
        )
        fetchPlans()
      }
    } catch (error) {
      console.error("Error disabling plan:", error)
      message.error(
        getPlansApiError(error, lang) ||
          t?.error_disabling_plan ||
          "Error disabling plan."
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleEnable = async (record) => {
    if (isReadOnly) {
      notifyReadOnly()
      return
    }
    if (!record?.id || record.status !== "Inactive") return

    setActionLoading(true)
    try {
      const response = await PlansApi.patchPlan(record.id, {
        status: "Active",
        active_status: true,
      })
      if (response?.status === 200) {
        message.success(
          getPlansApiSuccessMessage(
            response.data,
            lang,
            "Plan enabled successfully.",
            "تم تفعيل الباقة بنجاح."
          )
        )
        fetchPlans()
      }
    } catch (error) {
      console.error("Error enabling plan:", error)
      message.error(
        getPlansApiError(error, lang) ||
          t?.error_enabling_plan ||
          "Error enabling plan."
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = (record) => {
    if (!canDelete) {
      notifyDeleteBlocked()
      return
    }
    if (!record?.id) return

    Modal.confirm({
      title: t?.delete_confirm_title || "Delete plan?",
      content:
        t?.delete_confirm_message ||
        `Are you sure you want to delete "${record.name}"? This cannot be undone.`,
      okText: t?.actions?.delete || "Delete",
      okType: "danger",
      cancelText: t?.delete_cancel || "Cancel",
      onOk: async () => {
        setActionLoading(true)
        try {
          const response = await PlansApi.deletePlan(record.id)
          if (response?.status === 200) {
            message.success(
              getPlansApiSuccessMessage(
                response.data,
                lang,
                "Plan deleted successfully.",
                "تم حذف الباقة بنجاح."
              )
            )
            if (plans.length === 1 && page > 1) {
              setPage(page - 1)
            } else {
              fetchPlans()
            }
          }
        } catch (error) {
          console.error("Error deleting plan:", error)
          message.error(
            getPlansApiError(error, lang) ||
              t?.error_deleting_plan ||
              "Error deleting plan."
          )
        } finally {
          setActionLoading(false)
        }
      },
    })
  }

  return (
    <div className="plans-pricing-page-container">
      <div className="plans-pricing-page-header">
        <h1 className="plans-pricing-page-title">
          {t?.title || "Pricing & Plans"}
        </h1>
        <div className="d-flex align-items-center gap-2">
          {/* Usage statistics is read-only reporting, so it stays available to
              viewers who cannot edit plans. */}
          <Button
            icon={<BarChartOutlined />}
            onClick={() => navigate("/plans-pricing/usage")}
          >
            {translations[lang]?.planUsagePage?.usage_statistics ||
              "Usage Statistics"}
          </Button>
          {!isReadOnly ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreatePlan}
              className="plans-pricing-create-button"
            >
              {t?.create_plan || "Create Plan"}
            </Button>
          ) : null}
        </div>
      </div>

      <PlansPricingTable
        data={plans}
        loading={loading || actionLoading}
        isReadOnly={isReadOnly}
        canDelete={canDelete}
        searchValue={search}
        statusFilter={statusFilter}
        typeFilter={typeFilter}
        categoryFilter={categoryFilter}
        viewMode={viewMode}
        pagination={{
          current: page,
          pageSize: effectivePageSize,
          total,
        }}
        onPaginationChange={handlePaginationChange}
        onSearchChange={handleSearchChange}
        onStatusFilterChange={handleStatusFilterChange}
        onTypeFilterChange={handleTypeFilterChange}
        onCategoryFilterChange={handleCategoryFilterChange}
        onViewModeChange={handleViewModeChange}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDisable={handleDisable}
        onEnable={handleEnable}
        onDelete={handleDelete}
      />
    </div>
  )
}

export default PlansPricing
